import { useEffect, useState, useCallback } from 'react';
import pushNotificationService, { PushNotificationPayload } from '@/lib/firebase/messaging';
import { MessagePayload } from 'firebase/messaging';
import { toast } from 'react-toastify';

export interface UsePushNotificationsReturn {
  token: string | null;
  isSupported: boolean;
  permission: NotificationPermission | null;
  isLoading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  refreshToken: () => Promise<void>;
  requestPermission: () => Promise<NotificationPermission>;
}

/**
 * Hook for managing Firebase push notifications
 */
export default function usePushNotifications(): UsePushNotificationsReturn {
  const [token, setToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize push notifications
  const initialize = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if supported
      const supported = pushNotificationService.isSupported();
      setIsSupported(supported);

      if (!supported) {
        setError('Push notifications are not supported in this browser');
        setIsLoading(false);
        return;
      }

      // Check permission status before initialization
      const currentPermission = pushNotificationService.getPermissionStatus();
      setPermission(currentPermission);

      // Initialize service (this will request permission if needed)
      const fcmToken = await pushNotificationService.initialize();
      
      // Update permission status after initialization (permission may have changed)
      const updatedPermission = pushNotificationService.getPermissionStatus();
      setPermission(updatedPermission);
      
      // Set token and log if permission was granted
      if (fcmToken && updatedPermission === 'granted') {
        setToken(fcmToken);
        console.log('[usePushNotifications] ✅ Push notification token obtained successfully');
      } else if (fcmToken) {
        setToken(fcmToken);
      } else {
        setToken(null);
        if (updatedPermission === 'denied') {
          console.warn('[usePushNotifications] Permission denied - token not obtained');
        } else if (updatedPermission === 'default') {
          console.warn('[usePushNotifications] Permission not yet granted - token not obtained');
        }
      }

      // Set up token change listener
      pushNotificationService.onTokenChange((newToken) => {
        setToken(newToken);
      });

      // Set up message listener
      pushNotificationService.onMessage((payload) => {
        handleIncomingMessage(payload);
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize push notifications';
      setError(errorMessage);
      console.error('Push notification initialization error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle incoming push messages
  // Note: Toast notifications are already handled by onForegroundMessage in config.ts
  // This callback is for custom handling/logging only
  const handleIncomingMessage = useCallback((payload: MessagePayload) => {
    const notification = payload.notification;
    
    // Don't display toast here - it's already handled by onForegroundMessage
    // This prevents duplicate toasts from appearing
    console.log('[usePushNotifications] Message received (toast already displayed by foreground handler):', {
      title: notification?.title,
      messageId: payload.messageId,
    });

    // Handle custom data payload
    if (payload.data) {
      // You can handle custom actions here
      console.log('[usePushNotifications] Custom notification data:', payload.data);
    }
  }, []);

  // Refresh token
  const refreshToken = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const newToken = await pushNotificationService.refreshToken();
      setToken(newToken);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh token';
      setError(errorMessage);
      console.error('Token refresh error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Request permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    setIsLoading(true);
    setError(null);

    try {
      const { requestNotificationPermission } = await import('@/lib/firebase/config');
      const newPermission = await requestNotificationPermission();
      setPermission(newPermission);
      
      if (newPermission === 'granted') {
        // Initialize after permission is granted
        await initialize();
      }
      
      return newPermission;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to request permission';
      setError(errorMessage);
      console.error('Permission request error:', err);
      return 'denied';
    } finally {
      setIsLoading(false);
    }
  }, [initialize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pushNotificationService.cleanup();
    };
  }, []);

  return {
    token,
    isSupported,
    permission,
    isLoading,
    error,
    initialize,
    refreshToken,
    requestPermission,
  };
}

